import React, { useState, useEffect, useRef, useCallback } from 'react';
import CandidateSidebar from '../components/candidate/CandidateSidebar';
import CandidateMobileHeader from '../components/candidate/CandidateMobileHeader';
import CandidateMobileMenu from '../components/candidate/CandidateMobileMenu';
import JobAlertModal from '../components/candidate/JobAlertModal';
import { Bell, Plus, Edit2, Trash2, ToggleLeft, ToggleRight, Loader } from 'lucide-react';
import useResponsive from '../hooks/useResponsive';
import api from '../api/api';

const JobAlertsPage = () => {
  const { isMobile } = useResponsive();
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasFetchedRef = useRef(false);
  const [alerts, setAlerts] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Fetch job alerts ONCE on mount
  useEffect(() => {
    const fetchAlerts = async () => {
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true;
        setLoading(true);
        try {
          const response = await api.get('/job-alerts');
          setApiData(response.data);
          setError(null);
        }
        catch (err) {
          console.error('Failed to fetch job alerts:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAlerts();
  }, []);
  
  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/job-alerts');
      setApiData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Update alerts when API data is received
  useEffect(() => {
    if (apiData && apiData.alerts) {

      setAlerts(apiData.alerts);
    } else if (!loading) {

      setAlerts([]);
    }
  }, [apiData, loading]);

  const handleCreateAlert = async (alertData) => {
    try {
      setActionLoading('create');
      await api.post('/job-alerts', alertData);
      await refetch();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Failed to create alert:', err);
      alert('Failed to create job alert. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUpdateAlert = async (alertData) => {
    try {
      setActionLoading(`update-${alertData.id}`);
      await api.put('/job-alerts', alertData);
      await refetch();
      setEditingAlert(null);
    } catch (err) {
      console.error('Failed to update alert:', err);
      alert('Failed to update job alert. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAlert = async (alertId, currentStatus) => {
    try {
      setActionLoading(`toggle-${alertId}`);
      await api.put('/job-alerts', {
        id: alertId,
        enabled: !currentStatus
      });
      await refetch();
    } catch (err) {
      console.error('Failed to toggle alert:', err);
      alert('Failed to toggle job alert. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Are you sure you want to delete this job alert?')) {
      return;
    }
    
    try {
      setActionLoading(`delete-${alertId}`);
      await api.delete(`/job-alerts?id=${alertId}`);
      await refetch();
    } catch (err) {
      console.error('Failed to delete alert:', err);
      alert('Failed to delete job alert. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEditAlert = (alert) => {
    setEditingAlert(alert);
  };

  const getFrequencyBadge = (frequency) => {
    const colors = {
      daily: 'bg-green-100 text-green-800',
      weekly: 'bg-blue-100 text-blue-800',
      monthly: 'bg-purple-100 text-purple-800'
    };
    return colors[frequency] || colors.daily;
  };

  return (
    <div className="flex flex-col md:flex-row bg-white min-h-screen font-sans relative">
      {isMobile && <CandidateMobileHeader mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />}
      {isMobile && mobileMenuOpen && <CandidateMobileMenu setMobileMenuOpen={setMobileMenuOpen} />}
      <CandidateSidebar />
      
      <main className="flex-1 p-4 md:p-8 ml-0 md:ml-24">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-black mb-2">Job Alerts</h1>
            <p className="text-gray-600">Get notified when new jobs match your criteria</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Plus size={20} />
            <span className="hidden md:inline">Create Alert</span>
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading job alerts...</div>
          </div>
        ) : alerts.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Bell size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No job alerts yet</h3>
            <p className="text-gray-600 mb-6">Create your first alert to get notified about relevant jobs</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
            >
              Create Your First Alert
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {alerts.map((alert) => (
              <div 
                key={alert.id}
                className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-black mb-2">{alert.name}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {alert.keywords.map((keyword, idx) => (
                        <span 
                          key={idx}
                          className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleAlert(alert.id, alert.enabled)}
                    disabled={actionLoading === `toggle-${alert.id}`}
                    className="ml-2 disabled:opacity-50"
                  >
                    {actionLoading === `toggle-${alert.id}` ? (
                      <Loader size={32} className="animate-spin text-gray-400" />
                    ) : alert.enabled ? (
                      <ToggleRight size={32} className="text-green-500" />
                    ) : (
                      <ToggleLeft size={32} className="text-gray-400" />
                    )}
                  </button>
                </div>

                <div className="space-y-2 text-sm text-gray-600 mb-4">
                  {alert.location && (
                    <div>📍 <span className="font-medium">Location:</span> {alert.location}</div>
                  )}
                  {alert.jobType && alert.jobType !== 'Any' && (
                    <div>💼 <span className="font-medium">Type:</span> {alert.jobType}</div>
                  )}
                  {alert.salaryMin > 0 && (
                    <div>💰 <span className="font-medium">Min Salary:</span> ${alert.salaryMin.toLocaleString()}</div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getFrequencyBadge(alert.frequency)}`}>
                      {alert.frequency}
                    </span>
                    {alert.matchedJobsCount > 0 && (
                      <span className="text-sm text-gray-600">
                        {alert.matchedJobsCount} matches
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditAlert(alert)}
                      disabled={actionLoading === `update-${alert.id}`}
                      className="text-gray-600 hover:text-black p-2 disabled:opacity-50"
                      title="Edit"
                    >
                      {actionLoading === `update-${alert.id}` ? (
                        <Loader size={18} className="animate-spin" />
                      ) : (
                        <Edit2 size={18} />
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteAlert(alert.id)}
                      disabled={actionLoading === `delete-${alert.id}`}
                      className="text-red-500 hover:text-red-700 p-2 disabled:opacity-50"
                      title="Delete"
                    >
                      {actionLoading === `delete-${alert.id}` ? (
                        <Loader size={18} className="animate-spin" />
                      ) : (
                        <Trash2 size={18} />
                      )}
                    </button>
                  </div>
                </div>

                {alert.lastSent && (
                  <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                    Last sent: {new Date(alert.lastSent).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      <JobAlertModal
        isOpen={showCreateModal || editingAlert !== null}
        onClose={() => {
          setShowCreateModal(false);
          setEditingAlert(null);
        }}
        onSave={editingAlert ? handleUpdateAlert : handleCreateAlert}
        alertToEdit={editingAlert}
      />
    </div>
  );
};

export default JobAlertsPage;
